import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getLang } from "@/lib/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Lock, Trash2, Plus } from "lucide-react";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { brandVar, loadFont, FONT_OPTIONS } from "@/lib/color";
import { useBrand } from "@/context/BrandContext";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TEXT_FIELDS = [
  ["Brand", [["brand_name", "Brand name"], ["edition_label", "Edition label"], ["partner_name", "Partner name (blank for base edition)"]]],
  ["Course", [["course_title", "Course title"], ["subtitle", "Subtitle"], ["doctrine_name", "Doctrine name"]]],
  ["Certificate", [["certificate_issuer", "Certificate issuer"], ["verify_base_url", "Verification base URL (blank = this site /verify)"]]],
  ["Conversion CTA", [["cta_label", "Primary CTA label"], ["cta_url", "Primary CTA URL"], ["secondary_cta_label", "Secondary CTA label"]]],
  ["Legal & support", [["legal_url", "Legal URL"], ["privacy_url", "Privacy URL"], ["support_email", "Support email"]]],
];

const previewVars = (ed, dark) => {
  const out = {};
  ["primary", "accent", "data", "action"].forEach((k) => {
    const v = brandVar(ed.colors?.[k], dark);
    out[`--brand-${k}`] = v.value;
    out[`--brand-${k}-fg`] = v.fg;
  });
  out["--font-heading"] = `"${ed.heading_font}", sans-serif`;
  out["--font-body"] = `"${ed.body_font}", sans-serif`;
  return out;
};

const Preview = ({ ed, dark }) => (
  <div translate="no" data-testid={`theme-preview-${dark ? "dark" : "light"}`} className={`${dark ? "dark" : ""} border border-border`} style={previewVars(ed, dark)}>
    <div className="bg-background p-5 text-foreground" style={{ fontFamily: "var(--font-body)" }}>
      <div className="flex items-center justify-between border-b border-border pb-3">
        <span className={dark && ed.logo_shape === "square" ? "rounded-sm bg-white p-0.5" : ""}>{ed.logo_url ? <img src={assetUrl(ed.logo_url)} alt="logo preview" className="h-7 max-w-[150px] object-contain" /> : <b>{ed.brand_name}</b>}</span>
        <span className="font-mono text-[10px] uppercase text-muted-foreground">{dark ? "Dark" : "Light"}</span>
      </div>
      <div className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">{ed.edition_label}</div>
      <div className="mt-1 text-xl font-black uppercase leading-tight" style={{ fontFamily: "var(--font-heading)" }}>{ed.course_title}</div>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="rounded-sm bg-brand-primary px-3 py-1.5 text-xs font-semibold text-brand-primary-fg">Start</span>
        <span className="rounded-sm bg-brand-action px-3 py-1.5 text-xs font-semibold text-brand-action-fg">Certified quiz</span>
        <span className="rounded-sm border border-brand-data px-3 py-1.5 text-xs text-brand-data">Data</span>
        <span className="rounded-sm bg-brand-accent px-3 py-1.5 text-xs text-brand-accent-fg">Passed</span>
      </div>
    </div>
  </div>
);

const LogoUpload = ({ slug, onUploaded }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/editions/${slug}/logo`, fd);
      onUploaded(data.logo_url);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" data-testid="logo-file-input" onChange={(e) => upload(e.target.files[0])} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid="logo-upload-button" className="inline-flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50">
        <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload logo"}
      </button>
    </>
  );
};

const GlossaryOverrides = ({ items, onChange }) => (
  <div className="space-y-3">
    {items.map((g, i) => (
      <div key={i} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
        <Input data-testid={`glossary-override-term-${i}`} placeholder="Term" value={g.term} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, term: e.target.value } : x)))} />
        <Input data-testid={`glossary-override-def-${i}`} placeholder="Partner definition" value={g.definition} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, definition: e.target.value } : x)))} />
        <button type="button" aria-label="Remove term" onClick={() => onChange(items.filter((_, j) => j !== i))} className="flex h-10 w-10 items-center justify-center border border-border hover:bg-muted"><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <button type="button" data-testid="glossary-override-add-button" onClick={() => onChange([...items, { term: "", definition: "" }])} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-primary"><Plus className="h-4 w-4" /> Add override</button>
  </div>
);

const FontSelect = ({ id, value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={`font-select-${id}`} className="h-10 rounded-sm"><SelectValue /></SelectTrigger>
    <SelectContent>{FONT_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
  </Select>
);

export default function EditionEditor() {
  const { slug } = useParams();
  const qc = useQueryClient();
  const brand = useBrand();
  const [ed, setEd] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/editions").then(({ data }) => setEd(data.find((e) => e.slug === slug)));
  }, [slug]);
  useEffect(() => {
    if (ed) { loadFont(ed.heading_font); loadFont(ed.body_font); }
  }, [ed?.heading_font, ed?.body_font]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ed) return <Loader />;
  const set = (k, v) => setEd({ ...ed, [k]: v });
  const setColor = (k, v) => setEd({ ...ed, colors: { ...ed.colors, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const { history, slug: _s, is_default, logo_url, edition_version, created_at, ...body } = ed;
      const { data } = await api.put(`/admin/editions/${slug}`, body);
      setEd(data);
      qc.invalidateQueries({ queryKey: ["admin-editions"] });
      if (brand.slug === slug) brand.reload();
      toast.success(`Published v${data.edition_version}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const onLogo = (url) => { setEd({ ...ed, logo_url: url }); if (brand.slug === slug) brand.reload(); };

  return (
    <PageShell wide>
      <Link to="/admin" data-testid="editor-back-link" className="text-sm text-muted-foreground hover:text-foreground">← Editions</Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline-label">/e/{ed.slug} · v{ed.edition_version} · core content v1.0</div>
          <h1 data-testid="editor-title" className="mt-2 font-heading text-3xl font-black tracking-tight">{getLang() === "fr" ? `Édition ${ed.brand_name}` : `${ed.brand_name} edition`}</h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="edition-save-button" className="rounded-sm bg-brand-action px-6 py-2.5 font-semibold text-brand-action-fg hover:opacity-90 disabled:opacity-50">{saving ? "Publishing…" : "Save & publish version"}</button>
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <div className="space-y-10 lg:col-span-7">
          <section className="space-y-4">
            <h2 className="font-heading text-lg font-bold">Logo & theme</h2>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-16 min-w-[120px] items-center justify-center rounded-sm border border-border bg-white px-3">{ed.logo_url ? <img data-testid="editor-logo-preview" src={assetUrl(ed.logo_url)} alt="Current logo" className="h-12 max-w-[220px] object-contain" /> : <span className="text-sm text-neutral-500">No logo</span>}</div>
              <LogoUpload slug={slug} onUploaded={onLogo} />
              <Select value={ed.logo_shape || "wide"} onValueChange={(v) => set("logo_shape", v)}>
                <SelectTrigger data-testid="logo-shape-select" className="h-10 w-48 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="wide">Wordmark (logo only)</SelectItem><SelectItem value="square">Icon + brand name</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[["primary", "Primary"], ["accent", "Accent"], ["data", "Data / architecture"], ["action", "Action / value"]].map(([k, l]) => (
                <div key={k} className="space-y-2">
                  <Label htmlFor={`c-${k}`}>{l}</Label>
                  <div className="flex items-center gap-2">
                    <input id={`c-${k}`} type="color" data-testid={`color-${k}-input`} value={ed.colors?.[k] || "#000000"} onChange={(e) => setColor(k, e.target.value)} className="h-10 w-12 cursor-pointer border border-border bg-transparent" />
                    <span className="font-mono text-xs">{ed.colors?.[k]}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>Heading font</Label><FontSelect id="heading" value={ed.heading_font} onChange={(v) => set("heading_font", v)} /></div>
              <div className="space-y-2"><Label>Body font</Label><FontSelect id="body" value={ed.body_font} onChange={(v) => set("body_font", v)} /></div>
            </div>
          </section>
          {TEXT_FIELDS.map(([group, fields]) => (
            <section key={group} className="space-y-4">
              <h2 className="font-heading text-lg font-bold">{group}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {fields.map(([k, l]) => (
                  <div key={k} className="space-y-2"><Label htmlFor={`f-${k}`}>{l}</Label><Input id={`f-${k}`} data-testid={`edition-field-${k}`} value={ed[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-10 rounded-sm" /></div>
                ))}
              </div>
            </section>
          ))}
          <section className="space-y-4">
            <h2 className="font-heading text-lg font-bold">Partner introduction & examples</h2>
            <Textarea data-testid="edition-field-partner_intro" rows={3} value={ed.partner_intro || ""} onChange={(e) => set("partner_intro", e.target.value)} className="rounded-sm" />
            <Textarea data-testid="edition-field-examples" rows={3} placeholder="Partner case studies or plant terminology" value={ed.examples || ""} onChange={(e) => set("examples", e.target.value)} className="rounded-sm" />
          </section>
          <section className="space-y-4">
            <h2 className="font-heading text-lg font-bold">Glossary overrides</h2>
            <GlossaryOverrides items={ed.glossary_overrides || []} onChange={(v) => set("glossary_overrides", v)} />
          </section>
          <section className="space-y-2">
            <h2 className="font-heading text-lg font-bold">Status</h2>
            <Select value={ed.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="edition-status-select" className="h-10 w-48 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
            </Select>
          </section>
        </div>
        <aside className="space-y-6 lg:col-span-5">
          <div className="sticky top-24 space-y-4">
            <div className="overline-label">Live theme preview</div>
            <Preview ed={ed} dark={false} />
            <Preview ed={ed} dark />
            <div className="flex gap-3 border border-border bg-muted/40 p-4 text-xs text-muted-foreground"><Lock className="h-4 w-4 shrink-0" />Core lessons, 400-question answer keys, scoring rules (80%) and safety language are locked at core content v1.0.</div>
            <div className="border border-border bg-card p-4">
              <div className="overline-label">Version history</div>
              <ul data-testid="edition-history" className="mt-2 max-h-56 space-y-1 overflow-auto text-xs">
                {[...(ed.history || [])].reverse().map((h, i) => <li key={i} className="flex justify-between gap-2 border-b border-border py-1"><span className="font-mono">v{h.version}</span><span className="truncate">{h.changes.join(", ")}</span><span className="text-muted-foreground">{h.by}</span></li>)}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </PageShell>
  );
}
